import React, {useCallback, useState} from 'react'

import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react'

import {supabase} from '@/api'
import {Therapist} from '@/api/models'
import AsyncSelect from '@/common/async-select'
import {CommonModalProps} from '@/utils/types'

const CreateRelatedBlogPostTherapistModal = ({
  open,
  onClose,
  onComplete,
  blogPost,
}: CommonModalProps & {
  blogPost: number
}) => {
  const toast = useToast()

  const [selectedValue, setSelectedValue] = useState<Therapist | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const handleClose = useCallback(() => {
    setSelectedValue(null)
    setIsCreating(false)
    onClose()
  }, [onClose])

  const asyncSelectLoadOptions = useCallback(async (searchValue) => {
    try {
      const query = supabase.from('therapists').select('id, full_name').limit(5)
      if (searchValue) {
        query.like('full_name', `%${searchValue}%`)
      }
      const {data, error} = await query
      if (error) {
        throw error
      }
      return data
    } catch (e) {
      console.log(e)
    }
  }, [])

  const optionLabel = useCallback((o: Therapist) => o.full_name ?? '', [])
  const optionValue = useCallback((o: Therapist) => o.full_name ?? '', [])

  const handleSuggestionCreate = useCallback(async () => {
    if (!selectedValue) {
      return
    }

    try {
      setIsCreating(true)
      const {error} = await supabase
        .from('blog_post_suggested_therapists')
        .insert({blog_post: blogPost, therapist: selectedValue.id})
      if (error) {
        throw error
      }
      toast({
        isClosable: true,
        status: 'success',
        title: 'Dodano powiązanego terapeutę',
      })
      onComplete && onComplete()
    } catch (e) {
      console.error('Failed to add related therapist', e)
      toast({
        isClosable: true,
        status: 'error',
        title: 'Nie udało się dodać powiązanego terapeuty',
      })
    } finally {
      setIsCreating(false)
      handleClose()
    }
  }, [blogPost, handleClose, onComplete, selectedValue, toast])

  return (
    <Modal isOpen={open} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent minW="500">
        <ModalHeader>Dodaj powiązanego terapeutę</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <FormControl id="name">
            <FormLabel>Imię terapeuty</FormLabel>
            <AsyncSelect
              isDisabled={isCreating}
              value={selectedValue}
              defaultOptions={true}
              loadOptions={asyncSelectLoadOptions}
              getOptionLabel={optionLabel}
              getOptionValue={optionValue}
              onChange={setSelectedValue}
              placeholder="Imię..."
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={handleClose} isDisabled={isCreating}>
            Zamknij
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSuggestionCreate}
            isLoading={isCreating}
            isDisabled={!selectedValue}
          >
            Zatwierdź
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CreateRelatedBlogPostTherapistModal
